/* eslint-disable */
import { Box, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { StriimMessageBox } from "@striim/striim-ui-v2";
import { StriimButton, StriimTypography } from "@striim/striim-ui";

// Striim AI brand-gradient primary button — keeps the design-system StriimButton while applying
// the AI gradient that can't be passed via `sx` on StriimButton's custom variants.
const AIGradientButton = styled(StriimButton)({
    background:
        "linear-gradient(132deg, rgba(243, 23, 121, 0.80) 0.9%, rgba(121, 48, 217, 0.80) 66.21%, rgba(17, 0, 0, 0.80) 127.52%)",
    color: "#FFFFFF",
    border: "none",
    "&:disabled": { color: "#FFFFFF", opacity: 0.5 }
});
import App from "app";
import { getNameValidation } from "src/modules/apps/pages/create-app-name-app-page/name.validation";
import FormikField from "src/generic/formik-field";
import NamespaceSelect from "src/modules/dashboards/pages/dashboard-list/components/add-dashboard-dialog/components/namespace-select/namespace-select";
import NamespaceValidation from "src/modules/dashboards/pages/dashboard-list/components/add-dashboard-dialog/components/namespace-select/namespace.validation";
import { Formik, Form } from "formik";
import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { buildCancelledResponse } from "../utils/hitl-cancel";

export interface NameAppCheckerToolProps {
    args?: {
        suggestedName?: string;
        suggestedNamespace?: string;
    };
    respond?: (result: string) => void;
    /** Override the default namespace used when args.suggestedNamespace is empty. Useful in Storybook. */
    defaultNamespace?: string;
}
const validationSchema = Yup.object({
    name: getNameValidation(true, "APPLICATION"),
    nsName: NamespaceValidation
});

export const NameAppCheckerTool: React.FC<NameAppCheckerToolProps> = ({ args, respond, defaultNamespace }) => {
    const initialValues = useMemo(() => {
        const resolvedDefaultNamespace =
            args?.suggestedNamespace ||
            defaultNamespace ||
            (typeof App !== "undefined" && (App as any)?.user?.defaultNamespace) ||
            "admin";
        return {
            name: args?.suggestedName ?? "",
            nsName: resolvedDefaultNamespace
        };
    }, [args, defaultNamespace]);

    const [submitted, setSubmitted] = useState(false);
    const [submittedFqn, setSubmittedFqn] = useState<string | null>(null);

    return (
        <Box
            sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                mt: 1,
                backgroundColor: "background.paper",
                width: "100%"
            }}
        >
            <Box mb={2}>
                <StriimTypography variant="h2" sx={{ mb: 2, color: "greyscale.900" }}>
                    Name Your App
                </StriimTypography>

                <StriimTypography variant="body4" sx={{ mb: 2, color: "greyscale.700" }}>
                    Choose a unique name and a namespace for the new application. The name is checked against existing
                    applications in the same namespace.
                </StriimTypography>
            </Box>
            <Formik
                initialValues={initialValues}
                initialTouched={{ name: !!args?.suggestedName }}
                validationSchema={validationSchema}
                validateOnChange
                validateOnMount
                onSubmit={({ name, nsName }) => {
                    const fqn = `${nsName}.${name}`;
                    setSubmittedFqn(fqn);
                    setSubmitted(true);
                    respond?.(
                        JSON.stringify({
                            status: "available",
                            name,
                            nsName,
                            fullyQualifiedName: fqn
                        })
                    );
                }}
            >
                {({ isValid, isValidating, dirty, values }) => (
                    <Form style={{ width: "100%" }}>
                        <Box mb={2} sx={{ width: "100%" }}>
                            <FormikField
                                required
                                name="name"
                                label="Name"
                                dataTestId="name-app-checker-name"
                                helperText=""
                                disabled={submitted}
                            />
                        </Box>
                        <Box mb={2} sx={{ width: "100%" }}>
                            <NamespaceSelect
                                required
                                name="nsName"
                                label="Namespace"
                                doNotCreateNamespaces
                                disabled={submitted}
                                className=""
                                namespaceOptions={[]}
                            />
                        </Box>

                        {submitted && submittedFqn && (
                            <Box sx={{ mb: 2, width: "100%" }}>
                                <StriimMessageBox
                                    type="SUCCESS"
                                    text={`"${submittedFqn}" is available and selected.`}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                            {isValidating && <CircularProgress size={16} />}
                            <StriimButton
                                variant="secondary"
                                onClick={() => {
                                    respond?.(buildCancelledResponse());
                                }}
                                disabled={submitted}
                            >
                                Cancel
                            </StriimButton>
                            <AIGradientButton
                                type="submit"
                                variant="primary"
                                disabled={!isValid || submitted}
                                data-test-id="name-app-checker-submit"
                            >
                                Use this name
                            </AIGradientButton>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};
